import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useAnchor, useCTAButtonLink } from "hooks";
import { FullArrow } from "icons";
import { HeroSubsection6Content } from "types/graphql/generated";
interface Props {
  content: HeroSubsection6Content;
}

const HeroSubsection6: VFC<Props> = withErrorBoundary(
  ({
    content: {
      title,
      heroSubsection6buttonLabel,
      heroSubsection6ImageDesktop,
      heroSubsection6Tablet,
      heroSubsection6Mobile,
      heroSubsection6Description,
      heroSubsection6ButtonLink,
    },
  }) => {
    useAnchor("hero-subsection6");

    return (
      <StyledHeroSubsection6
        className={cn("hero-subsection6", {
          HeroSubsection6_hasTabletImage: heroSubsection6Tablet.data,
          HeroSubsection6_hasMobileImage: heroSubsection6Mobile.data,
        })}
      >
        <div className="HeroSubsection6__background" />
        <div className="Content">
          <div className="HeroSubsection6__stepBlock">
            <div className="HeroSubsection6__textSection">
              <Markdown className="HeroSubsection6__title Font_24_32_600">{title}</Markdown>
              <PipelineRoutingLinkButton
                href={useCTAButtonLink(heroSubsection6ButtonLink)}
                className="HeroSubsection6__button"
                rightIcon={<FullArrow />}
              >
                {heroSubsection6buttonLabel}
              </PipelineRoutingLinkButton>
              <Markdown className="HeroSubsection6__description Color_tertiary Font_12_16_450">
                {heroSubsection6Description}
              </Markdown>
            </div>
          </div>
          <LegacyImage
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            {...heroSubsection6ImageDesktop.data.heroSubsection6ImageDesktop}
            className="HeroSubsection6__image"
          />

          {!!heroSubsection6Tablet.data && (
            <LegacyImage
              layout="fill"
              objectFit="cover"
              src={heroSubsection6Tablet.data.heroSubsection6Tablet.src}
              alt={heroSubsection6Tablet.data.heroSubsection6Tablet.alt}
              objectPosition="center"
              className="HeroSubsection6__imageTablet"
            />
          )}

          {!!heroSubsection6Mobile.data && (
            <LegacyImage
              layout="fill"
              objectFit="cover"
              src={heroSubsection6Mobile.data.heroSubsection6Mobile.src}
              alt={heroSubsection6Mobile.data.heroSubsection6Mobile.alt}
              objectPosition="center"
              className="HeroSubsection6__imageMobile"
            />
          )}
        </div>
      </StyledHeroSubsection6>
    );
  },
  {}
);

const StyledHeroSubsection6 = styled.section`
  position: relative;
  margin-top: -64px;
  padding-top: 64px;

  .HeroSubsection6__background {
    position: absolute;
    width: 100%;
    height: calc(100% - 120px);
    z-index: 0;
    background: ${({ theme }) => theme.colors.coral["05"]};
  }

  .Content {
    position: relative;
    display: flex;
    z-index: 1;
  }

  .HeroSubsection6__textSection {
    padding: 40px 24px;
    background: ${({ theme }) => theme.colors.fog["00"]};
  }

  .HeroSubsection6__stepBlock {
    padding: 32px;
    background: url("/images/noise.png"),
      radial-gradient(49.55% 37.88% at 74.19% 51.44%, rgb(85 50 250 / 20%) 17.04%, rgb(205 216 220 / 0%) 98.18%),
      radial-gradient(85.93% 39.2% at 91.09% 52.37%, #ffcd85 0%, rgb(252 207 143 / 0%) 100%),
      radial-gradient(99.76% 40.23% at 36.62% 90.65%, #5532fa 6.99%, #efa085 66.87%, #fcdac4 100%);
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
  }

  .HeroSubsection6__image {
    position: relative;
    width: 100%;
  }

  .HeroSubsection6__imageTablet {
    display: none;
  }

  .HeroSubsection6__imageMobile {
    display: none;
  }

  .HeroSubsection6__listItem {
    display: flex;
  }

  .HeroSubsection6__listItem:not(:first-child) {
    margin-top: 12px;
  }

  .HeroSubsection6__listNumber {
    width: 56px;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.fog["80"]};
    border-radius: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }

  .HeroSubsection6__listItemStep {
    align-self: center;
    margin-left: 16px;
  }

  .HeroSubsection6__button {
    padding: 15px 32px;
    margin-top: 32px;
  }

  .HeroSubsection6__description {
    width: 368px;
    margin-top: 16px;
  }

  @media (min-width: 1200px) {
    .HeroSubsection6__stepBlock {
      background-size: 100% 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .HeroSubsection6__description {
      width: 320px;
    }

    &.HeroSubsection6_hasTabletImage {
      .HeroSubsection6__image {
        display: none;
      }

      .HeroSubsection6__imageTablet {
        position: relative;
        width: 100%;
        display: block;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .HeroSubsection6__stepBlock {
      padding: 24px;
    }

    &.HeroSubsection6_hasMobileImage {
      .HeroSubsection6__imageTablet {
        display: none;
      }

      .HeroSubsection6__imageMobile {
        position: relative;
        width: 100%;
        display: block;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Content {
      padding: 0;
    }

    .HeroSubsection6__stepBlock {
      width: 100%;
      padding: 16px;
    }

    .HeroSubsection6__LegacyImage .HeroSubsection6__imageTablet,
    .HeroSubsection6__imageMobile {
      display: none !important;
    }

    .HeroSubsection6__description {
      width: 100%;
    }
  }
`;

gql`
  fragment HeroSubsection6Content on ComponentHeroHeroSubsection6 {
    title
    heroSubsection6buttonLabel
    heroSubsection6ButtonLink
    heroSubsection6Description
    heroSubsection6ImageDesktop {
      data {
        heroSubsection6ImageDesktop: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    heroSubsection6Tablet {
      data {
        heroSubsection6Tablet: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    heroSubsection6Mobile {
      data {
        heroSubsection6Mobile: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
  }
`;

export { HeroSubsection6 };
export type { Props as HeroSubsection6Props };
